import { queryOptions } from "@tanstack/react-query";

import { endOfMonth } from "date-fns";

import {
  reconcile,
  type CalendarSync,
  type ChantierCalendarData,
} from "@/lib/chantier-calendar/sync";
import type { HolidayPreviewItem } from "@/lib/chantier-calendar/service.server";
import type { CalendarPlace, CalendarSupplier } from "@/lib/chantier-calendar/types";
import type { WeatherAlertsResult } from "@/lib/chantier-calendar/alerts";
import { formatDay, parseDay } from "@/lib/chantier-calendar/dates";
import type { WastedDaysReport } from "@/lib/chantier-calendar/wasted-days";
import type { DayWeatherResult } from "@/lib/chantier-calendar/weather";
import type { CalendarTemplate } from "@/lib/chantier-calendar/templates";
import type { CalendarContact } from "@/lib/chantier-calendar/email";
import type { HourlyWeatherResult } from "@/lib/chantier-calendar/weather-hourly";

// Wire shapes of /api/chantiers: the server module exports the types, the
// browser only imports them as types (no server code reaches the bundle).
export type ChantierListItem = {
  id: string;
  name: string;
  projectNumber: string;
  place: string | null;
  suppliers: number;
  upcomingBookings: number;
  supplierPreview: CalendarSupplier[];
  nextBookings: Array<{ id: string; title: string; startDate: string; endDate: string }>;
  weatherToday: Array<{ supplier: CalendarSupplier; messages: string[] }>;
};

export type { ChantierCalendarData };

export const chantierKeys = {
  all: ["chantiers"] as const,
  list: () => ["chantiers", "list"] as const,
  calendar: (projectId: string) => ["chantiers", "calendar", projectId] as const,
  weather: (projectId: string, date: string) =>
    ["chantiers", "calendar", projectId, "weather", date] as const,
  alerts: (projectId: string) => ["chantiers", "calendar", projectId, "alerts"] as const,
  wastedDays: (projectId: string, month: string) =>
    ["chantiers", "calendar", projectId, "wasted-days", month] as const,
  leaveNames: (projectId: string) => ["chantiers", "calendar", projectId, "leave-names"] as const,
  places: (projectId: string, query: string) =>
    ["chantiers", "calendar", projectId, "places", query] as const,
  holidays: (projectId: string, year: number) =>
    ["chantiers", "calendar", projectId, "holidays", year] as const,
  templates: (projectId: string) => ["chantiers", "calendar", projectId, "templates"] as const,
  contacts: (projectId: string) => ["chantiers", "calendar", projectId, "contacts"] as const,
  hourly: (projectId: string, from: string, to: string) =>
    ["chantiers", "calendar", projectId, "weather", "hourly", from, to] as const,
};

/** JSON helper that throws the server's French message, as the query contract wants. */
export async function chantierApi<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: { "content-type": "application/json", ...init?.headers },
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    // SAFETY: every /api/chantiers error answers the route envelope { success: false, message }.
    const envelope = payload as { message?: string } | null;
    throw new Error(envelope?.message ?? "Une erreur est survenue. Réessayez.");
  }

  // SAFETY: callers name the success shape of the endpoint they call; the server owns it.
  return payload as T;
}

export const chantiersQueryOptions = () =>
  queryOptions({
    queryKey: chantierKeys.list(),
    queryFn: () =>
      chantierApi<{ today: string; chantiers: ChantierListItem[]; preview: boolean }>(
        "/api/chantiers",
      ),
    // A terminal 404 means the flag is off for this user; retrying cannot help.
    retry: false,
  });

/**
 * The calendar. It asks the server only for what changed after the revision it
 * already holds, so every refetch (a change from another tab, a reconnection,
 * an invalidation) costs a few bytes when nothing moved and only the touched
 * entries when something did. The very first read is the whole calendar.
 */
export const chantierCalendarQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: chantierKeys.calendar(projectId),
    queryFn: async ({ client }) => {
      const url = `/api/chantiers/${projectId}/calendrier`;
      const cached = client.getQueryData<ChantierCalendarData>(chantierKeys.calendar(projectId));

      const sync = await chantierApi<CalendarSync>(
        cached ? `${url}?since=${cached.revision}` : url,
      );

      return reconcile(cached, sync);
    },
    retry: false,
  });

export const holidayPreviewQueryOptions = (projectId: string, year: number) =>
  queryOptions({
    queryKey: chantierKeys.holidays(projectId, year),
    queryFn: async () =>
      (
        await chantierApi<{ holidays: HolidayPreviewItem[] }>(
          `/api/chantiers/${projectId}/calendrier/jours-feries?year=${year}`,
        )
      ).holidays,
    retry: false,
  });

/** Who the closures of a month cost visits to, read for the lost-days report. */
export const wastedDaysQueryOptions = (projectId: string, month: string) =>
  queryOptions({
    queryKey: chantierKeys.wastedDays(projectId, month),
    queryFn: () => {
      const first = parseDay(`${month}-01`);

      const query = new URLSearchParams({
        from: formatDay(first),
        to: formatDay(endOfMonth(first)),
      });

      return chantierApi<WastedDaysReport>(
        `/api/chantiers/${projectId}/calendrier/jours-perdus?${query}`,
      );
    },
    retry: false,
  });

/** The names already used for staff leave on the chantier, to suggest them in the form. */
export const leaveNamesQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: chantierKeys.leaveNames(projectId),
    queryFn: async () =>
      (await chantierApi<{ names: string[] }>(`/api/chantiers/${projectId}/calendrier/personnel`))
        .names,
    retry: false,
  });

/** One day's weather, read when the day is opened (the place comes from the chantier settings). */
export const dayWeatherQueryOptions = (projectId: string, date: string) =>
  queryOptions({
    queryKey: chantierKeys.weather(projectId, date),
    queryFn: () =>
      chantierApi<DayWeatherResult>(`/api/chantiers/${projectId}/calendrier/meteo?date=${date}`),
    // The forecast moves slowly; the server caches it for an hour as well.
    staleTime: 30 * 60_000,
    retry: false,
  });

export const placeSearchQueryOptions = (projectId: string, query: string) =>
  queryOptions({
    queryKey: chantierKeys.places(projectId, query),
    queryFn: async () =>
      (
        await chantierApi<{ places: CalendarPlace[] }>(
          `/api/chantiers/${projectId}/calendrier/lieux?q=${encodeURIComponent(query)}`,
        )
      ).places,
    staleTime: 10 * 60_000,
    retry: false,
  });

/** Active weather alerts at the chantier, refreshed every few minutes while the calendar is open. */
export const weatherAlertsQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: chantierKeys.alerts(projectId),
    queryFn: () =>
      chantierApi<WeatherAlertsResult>(`/api/chantiers/${projectId}/calendrier/alertes`),
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
    retry: false,
  });

export const calendarRestoreQueryOptions = (projectId: string, at?: string) =>
  queryOptions({
    queryKey: [...chantierKeys.calendar(projectId), "restore", at ?? "bounds"],
    queryFn: () =>
      chantierApi<import("@/lib/chantier-calendar/restore").RestorePreview>(
        `/api/chantiers/${projectId}/calendrier/restauration${at ? `?at=${encodeURIComponent(at)}` : ""}`,
      ),
    staleTime: 0,
    retry: false,
  });

/** The chantier's saved views and print presets; read only when the person has the flag. */
export const calendarTemplatesQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: chantierKeys.templates(projectId),
    queryFn: async () =>
      (
        await chantierApi<{ templates: CalendarTemplate[] }>(
          `/api/chantiers/${projectId}/calendrier/modeles`,
        )
      ).templates,
    retry: false,
  });

/** The chantier's distribution list; read only with the email module's flag. */
export const calendarContactsQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: chantierKeys.contacts(projectId),
    queryFn: async () =>
      (
        await chantierApi<{ contacts: CalendarContact[] }>(
          `/api/chantiers/${projectId}/calendrier/contacts`,
        )
      ).contacts,
    retry: false,
  });

/** The chantier's weather hour by hour (module "Météo horaire"): a day for its detail, two weeks for the weather rules. */
export const hourlyWeatherQueryOptions = (projectId: string, from: string, to: string) =>
  queryOptions({
    queryKey: chantierKeys.hourly(projectId, from, to),
    queryFn: () =>
      chantierApi<HourlyWeatherResult>(
        `/api/chantiers/${projectId}/calendrier/meteo/horaire?from=${from}&to=${to}`,
      ),
    // The forecast moves slowly; the server caches it for an hour as well.
    staleTime: 30 * 60_000,
    retry: false,
  });
